import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import FeatureGate from '@/components/FeatureGate';
import { isCalibrationEnabled } from '@/utils/features';

// Configure axios with retry logic for resilience
axios.defaults.timeout = 30000; // 30 second timeout

// Add retry interceptor for failed requests (GET only - never retry mutations)
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // NEVER retry POST/PUT/DELETE - data may already be written to DB
    const method = (config?.method || '').toLowerCase();
    if (method !== 'get') {
      return Promise.reject(error);
    }
    
    // Don't retry if it's a 4xx error (client error) or already retried 3 times
    if (!config || config.__retryCount >= 3 || 
        (error.response && error.response.status >= 400 && error.response.status < 500)) {
      return Promise.reject(error);
    }
    
    // Retry GET requests on network errors or 5xx server errors
    if (!error.response || error.response.status >= 500) {
      config.__retryCount = config.__retryCount || 0;
      config.__retryCount += 1;
      
      // Wait before retry (exponential backoff: 1s, 2s, 4s)
      const delay = Math.pow(2, config.__retryCount - 1) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
      
      console.log(`Retrying GET request (attempt ${config.__retryCount}/3):`, config.url);
      return axios(config);
    }
    
    return Promise.reject(error);
  }
);

// Critical pages loaded immediately
import Login from '@/pages/Login';
import InlineQuality from '@/pages/InlineQuality';
import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';

// Lazy load all other pages for faster initial load
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const SupervisorDashboard = lazy(() => import('@/pages/SupervisorDashboard'));
const Jobs = lazy(() => import('@/pages/Jobs'));
const JobCreate = lazy(() => import('@/pages/JobCreate'));
const JobEdit = lazy(() => import('@/pages/JobEdit'));
const JobCard = lazy(() => import('@/pages/JobCard'));
const ShiftReports = lazy(() => import('@/pages/ShiftReports'));
const DispatchReports = lazy(() => import('@/pages/DispatchReports'));
const Machines = lazy(() => import('@/pages/Machines'));
const MachineCreate = lazy(() => import('@/pages/MachineCreate'));
const MachineEdit = lazy(() => import('@/pages/MachineEdit'));
const Users = lazy(() => import('@/pages/UsersNew'));
const DrawingManagement = lazy(() => import('@/pages/DrawingManagement'));
const GRNManagement = lazy(() => import('@/pages/GRNManagement'));
const RawMaterialInventory = lazy(() => import('@/pages/RawMaterialInventory'));
const WIPInventory = lazy(() => import('@/pages/WIPInventory'));
const QualityGateInspection = lazy(() => import('@/pages/QualityGateInspection'));
const QualityGateReports = lazy(() => import('@/pages/QualityGateReports'));
const QualityAnalytics = lazy(() => import('@/pages/QualityAnalytics'));
const HeatTreatmentReport = lazy(() => import('@/pages/HeatTreatmentReport'));
const SupervisorAssignments = lazy(() => import('@/pages/SupervisorAssignments'));
const DailyProductionEntry = lazy(() => import('@/pages/DailyProductionEntry'));
const AdminSettings = lazy(() => import('@/pages/AdminSettings'));
const AuditLogs = lazy(() => import('@/pages/AuditLogs'));
const Employees = lazy(() => import('@/pages/Employees'));
const Attendance = lazy(() => import('@/pages/Attendance'));
const EmployeeForm = lazy(() => import('@/pages/EmployeeForm'));
const EmployeeDetail = lazy(() => import('@/pages/EmployeeDetail'));
const MaintenanceDashboard = lazy(() => import('@/pages/MaintenanceDashboard'));
const BreakdownForm = lazy(() => import('@/pages/BreakdownForm'));
const BreakdownList = lazy(() => import('@/pages/BreakdownList'));
const BreakdownDetail = lazy(() => import('@/pages/BreakdownDetail'));
const MachineHistoryPage = lazy(() => import('@/pages/MachineHistory'));
const MaintenanceAnalytics = lazy(() => import('@/pages/MaintenanceAnalytics'));

// Calibration Module
const CalibrationDashboard = lazy(() => import('@/pages/CalibrationDashboard'));
const GaugeRegister = lazy(() => import('@/pages/GaugeRegister'));
const GaugeDetail = lazy(() => import('@/pages/GaugeDetail'));
const GaugeForm = lazy(() => import('@/pages/GaugeForm'));
const GaugeFamilies = lazy(() => import('@/pages/GaugeFamilies'));
const CalibrationSchedule = lazy(() => import('@/pages/CalibrationSchedule'));
const CalibrationSettings = lazy(() => import('@/pages/CalibrationSettings'));
const MyGauges = lazy(() => import('@/pages/MyGauges'));
const IssuanceLog = lazy(() => import('@/pages/IssuanceLog'));

// Error Boundary for graceful error handling
import ErrorBoundary from '@/components/ErrorBoundary';
import EnableNotificationsModal from '@/components/EnableNotificationsModal';
import { attachResubscribeListener } from '@/utils/pushNotifications';
import { initNotificationSound } from '@/utils/notificationSound';

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="text-lg text-slate-600">Loading...</div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
    // Wire up SW → app re-subscribe channel (one-time, idempotent)
    attachResubscribeListener();
    // Wire up foreground in-app notification tone (one-time, idempotent)
    initNotificationSound();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
            
            {user ? (
              <Route path="/" element={<Layout user={user} onLogout={handleLogout} />}>
                <Route index element={
                  user.role === 'Supervisor' ? <SupervisorDashboard /> :
                  user.role === 'MaintenanceTechnician' ? <Navigate to="/maintenance" replace /> :
                  user.role === 'HRofficer' ? <Navigate to="/employees" replace /> :
                  <Dashboard />
                } />
              <Route path="supervisor-dashboard" element={<SupervisorDashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              
              <Route path="daily-production-entry" element={<DailyProductionEntry />} />
              
              <Route path="jobs" element={
                <ProtectedRoute user={user} requiredPermission="jobCards.view">
                  <Jobs />
                </ProtectedRoute>
              } />
              <Route path="jobs/create" element={
                <ProtectedRoute user={user} requiredPermission="jobCards.create">
                  <JobCreate />
                </ProtectedRoute>
              } />
              <Route path="jobs/:jobId/edit" element={
                <ProtectedRoute user={user} requiredPermission="jobCards.edit">
                  <JobEdit />
                </ProtectedRoute>
              } />
              <Route path="jobs/:jobId" element={
                <ProtectedRoute user={user} requiredPermission="jobCards.view">
                  <JobCard />
                </ProtectedRoute>
              } />
              
              <Route path="shift-reports" element={
                <ProtectedRoute user={user} requiredPermission="shiftReports.view">
                  <ShiftReports />
                </ProtectedRoute>
              } />
              <Route path="dispatch-reports" element={
                <ProtectedRoute user={user} requiredPermission="dispatchReports.view">
                  <DispatchReports />
                </ProtectedRoute>
              } />
              
              <Route path="wip-inventory" element={
                <ProtectedRoute user={user} requiredPermission="wipInventory.view">
                  <WIPInventory />
                </ProtectedRoute>
              } />
              <Route path="raw-material-inventory" element={
                <ProtectedRoute user={user} requiredPermission="rawMaterialInventory.view">
                  <RawMaterialInventory />
                </ProtectedRoute>
              } />
              
              <Route path="machines" element={
                <ProtectedRoute user={user} requiredPermission="machines.view">
                  <Machines />
                </ProtectedRoute>
              } />
              <Route path="machines/create" element={
                <ProtectedRoute user={user} requiredPermission="machines.create">
                  <MachineCreate />
                </ProtectedRoute>
              } />
              <Route path="machines/edit/:machineId" element={
                <ProtectedRoute user={user} requiredPermission="machines.edit">
                  <MachineEdit />
                </ProtectedRoute>
              } />
              
              <Route path="users" element={
                <ProtectedRoute user={user} requiredPermission="userManagement.view">
                  <Users />
                </ProtectedRoute>
              } />
              
              <Route path="drawing-management" element={
                <ProtectedRoute user={user} requiredPermission="drawingManagement.view">
                  <DrawingManagement />
                </ProtectedRoute>
              } />
              
              <Route path="grn" element={
                <ProtectedRoute user={user} requiredPermission="grn.view">
                  <GRNManagement />
                </ProtectedRoute>
              } />
              
              <Route path="raw-material-inventory" element={
                <ProtectedRoute user={user} requiredPermission="rawMaterialInventory.view">
                  <RawMaterialInventory />
                </ProtectedRoute>
              } />
              <Route path="wip-inventory" element={
                <ProtectedRoute user={user} requiredPermission="wipInventory.view">
                  <WIPInventory />
                </ProtectedRoute>
              } />
              
              <Route path="quality-gate-inspection" element={
                <ProtectedRoute user={user} requiredPermission="qualityGate.view">
                  <QualityGateInspection />
                </ProtectedRoute>
              } />
              <Route path="quality-gate-reports" element={
                <ProtectedRoute user={user} requiredPermission="qualityGate.reports">
                  <QualityGateReports />
                </ProtectedRoute>
              } />
              <Route path="quality-analytics" element={
                <ProtectedRoute user={user} requiredPermission="qualityAnalytics.any">
                  <QualityAnalytics />
                </ProtectedRoute>
              } />
              <Route path="heat-treatment-report" element={
                <ProtectedRoute user={user} requiredRoles={['Admin']}>
                  <HeatTreatmentReport />
                </ProtectedRoute>
              } />
              <Route path="inline-quality" element={
                <ProtectedRoute user={user} requiredPermission="inlineQuality.view">
                  <InlineQuality />
                </ProtectedRoute>
              } />
              
              <Route path="supervisor-assignments" element={
                <ProtectedRoute user={user} requiredPermission="supervisorManagement.view">
                  <SupervisorAssignments />
                </ProtectedRoute>
              } />
              <Route path="admin-settings" element={
                <AdminSettings />
              } />
              <Route path="audit-logs" element={
                <AuditLogs />
              } />
              <Route path="employees" element={
                <ProtectedRoute user={user} requiredPermission="employees.view">
                  <Employees />
                </ProtectedRoute>
              } />
              <Route path="attendance" element={
                <ProtectedRoute user={user} requiredPermission="employees.view">
                  <Attendance />
                </ProtectedRoute>
              } />
              <Route path="employees/new" element={<Navigate to="/employees" replace />} />
              <Route path="employees/:id" element={<Navigate to="/employees" replace />} />
              <Route path="employees/:id/edit" element={<Navigate to="/employees" replace />} />
              
              {/* Maintenance Module */}
              <Route path="maintenance" element={
                <ProtectedRoute user={user} requiredPermission="breakdowns.view">
                  <MaintenanceDashboard />
                </ProtectedRoute>
              } />
              <Route path="maintenance/breakdowns" element={
                <ProtectedRoute user={user} requiredPermission="breakdowns.view">
                  <BreakdownList />
                </ProtectedRoute>
              } />
              <Route path="maintenance/report-breakdown" element={
                <ProtectedRoute user={user} requiredPermission="breakdowns.report">
                  <BreakdownForm />
                </ProtectedRoute>
              } />
              <Route path="maintenance/breakdowns/:bdId" element={
                <ProtectedRoute user={user} requiredPermission="breakdowns.view">
                  <BreakdownDetail />
                </ProtectedRoute>
              } />
              <Route path="maintenance/machine-history" element={
                <ProtectedRoute user={user} requiredPermission="machineHistory.view">
                  <MachineHistoryPage />
                </ProtectedRoute>
              } />
              <Route path="maintenance/machine-history/:machineId" element={
                <ProtectedRoute user={user} requiredPermission="machineHistory.view">
                  <MachineHistoryPage />
                </ProtectedRoute>
              } />
              <Route path="maintenance/analytics" element={
                <ProtectedRoute user={user} requiredPermission="maintenanceAnalytics.view">
                  <MaintenanceAnalytics />
                </ProtectedRoute>
              } />

              {/* Calibration Module */}
              <Route path="calibration" element={
                <FeatureGate enabled={isCalibrationEnabled()}>
                  <ProtectedRoute user={user} requiredPermission="calibration.view">
                    <CalibrationDashboard />
                  </ProtectedRoute>
                </FeatureGate>
              } />
              <Route path="calibration/gauges" element={
                <FeatureGate enabled={isCalibrationEnabled()}>
                  <ProtectedRoute user={user} requiredPermission="calibration.view">
                    <GaugeRegister />
                  </ProtectedRoute>
                </FeatureGate>
              } />
              <Route path="calibration/gauges/new" element={
                <FeatureGate enabled={isCalibrationEnabled()}>
                  <ProtectedRoute user={user} requiredPermission="calibration.register">
                    <GaugeForm />
                  </ProtectedRoute>
                </FeatureGate>
              } />
              <Route path="calibration/gauges/:gaugeId" element={
                <FeatureGate enabled={isCalibrationEnabled()}>
                  <ProtectedRoute user={user} requiredPermission="calibration.view_any">
                    <GaugeDetail />
                  </ProtectedRoute>
                </FeatureGate>
              } />
              <Route path="calibration/families" element={
                <FeatureGate enabled={isCalibrationEnabled()}>
                  <ProtectedRoute user={user} requiredPermission="calibration.configure">
                    <GaugeFamilies />
                  </ProtectedRoute>
                </FeatureGate>
              } />
              <Route path="calibration/schedule" element={
                <FeatureGate enabled={isCalibrationEnabled()}>
                  <ProtectedRoute user={user} requiredPermission="calibration.view">
                    <CalibrationSchedule />
                  </ProtectedRoute>
                </FeatureGate>
              } />
              <Route path="calibration/issuances" element={
                <FeatureGate enabled={isCalibrationEnabled()}>
                  <ProtectedRoute user={user} requiredPermission="calibration.view">
                    <IssuanceLog />
                  </ProtectedRoute>
                </FeatureGate>
              } />
              <Route path="calibration/my-gauges" element={
                <FeatureGate enabled={isCalibrationEnabled()}>
                  <ProtectedRoute user={user} requiredPermission="calibration.view_any">
                    <MyGauges />
                  </ProtectedRoute>
                </FeatureGate>
              } />
              <Route path="calibration/settings" element={
                <FeatureGate enabled={isCalibrationEnabled()}>
                  <ProtectedRoute user={user} requiredPermission="calibration.configure">
                    <CalibrationSettings />
                  </ProtectedRoute>
                </FeatureGate>
              } />
            </Route>
          ) : (
            <Route path="*" element={<Navigate to="/login" />} />
          )}
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" />
      {user && <EnableNotificationsModal user={user} />}
    </div>
    </ErrorBoundary>
  );
}

export default App;
